import { useState, useCallback } from 'react'
import { supabase } from '@/lib/supabase'

export type EmployeeRoleCategory = 'instructor' | 'administrator' | 'receptionist' | 'finance' | 'other'
export type EmployeeEmploymentType = 'full_time' | 'part_time' | 'casual'
export type EmployeeCompensationModel = 'hourly' | 'salary'
export type EmployeePaymentFrequency = 'weekly' | 'biweekly' | 'monthly'

export interface EmployeeInviteFields {
  email: string
  fullLegalName: string
  jobTitle: string
  roleCategory: EmployeeRoleCategory | ''
  startDate: string
  employmentType: EmployeeEmploymentType | ''
  phone: string
  canViewStudents: boolean
  canViewSchedules: boolean
  canViewFinancials: boolean
  ontarioDriverLicenceNumber: string
  driverLicenceExpiryDate: string
  instructorLicenceNumber: string
  instructorLicenceExpiryDate: string
  instructionTypes: string[]
  address: string
  assignedVehicleId: string | null
  compensationModel: EmployeeCompensationModel | ''
  hourlyRate: string
  salaryAmount: string
  compensationEffectiveDate: string
  paymentFrequency: EmployeePaymentFrequency | ''
  photoPath: string | null
}

interface CreateResult {
  ok: boolean
  code?: string
  emailed?: boolean
  token?: string
  expires_at?: string
  warning?: string
}

/**
 * Create action for a richer "Add employee" invitation (migrations 083/084) — calls the
 * invite-employee Edge Function, which in turn calls create_employee_invitation(). This
 * is create-only; the pending/accepted/revoked list itself is still served by
 * useInstructorInvitations.ts (instructor_invitations, unchanged), since every employee
 * invitation is still a row in that same table — this hook only adds the richer intake
 * form's submit action on top.
 */
export function useEmployeeInvitation() {
  const [creating, setCreating] = useState(false)

  const createInvitation = useCallback(async (
    schoolId: string, fields: EmployeeInviteFields,
  ): Promise<CreateResult> => {
    if (creating) return { ok: false, code: 'busy' }
    setCreating(true)
    try {
      const { data, error: fnError } = await supabase.functions.invoke('invite-employee', {
        body: {
          schoolId,
          email: fields.email,
          fullLegalName: fields.fullLegalName,
          jobTitle: fields.jobTitle,
          roleCategory: fields.roleCategory,
          startDate: fields.startDate,
          employmentType: fields.employmentType,
          phone: fields.phone,
          canViewStudents: fields.canViewStudents,
          canViewSchedules: fields.canViewSchedules,
          canViewFinancials: fields.canViewFinancials,
          ontarioDriverLicenceNumber: fields.ontarioDriverLicenceNumber,
          driverLicenceExpiryDate: fields.driverLicenceExpiryDate,
          instructorLicenceNumber: fields.instructorLicenceNumber,
          instructorLicenceExpiryDate: fields.instructorLicenceExpiryDate,
          instructionTypes: fields.instructionTypes,
          address: fields.address,
          assignedVehicleId: fields.assignedVehicleId,
          compensationModel: fields.compensationModel,
          hourlyRate: fields.hourlyRate,
          salaryAmount: fields.salaryAmount,
          compensationEffectiveDate: fields.compensationEffectiveDate,
          paymentFrequency: fields.paymentFrequency,
          photoPath: fields.photoPath,
        },
      })
      if (fnError) return { ok: false, code: 'internal_error' }
      return data as CreateResult
    } finally {
      setCreating(false)
    }
  }, [creating])

  return { creating, createInvitation }
}
